/* eslint-disable max-lines */
/* eslint-disable no-shadow */
import {
  call, fork, join, put, race, take, takeEvery 
} from "redux-saga/effects";

import {
  addRoleToComposite,
  changeDocumentDefaultPermission,
  createCompoundRole,
  createDocumentGroup,
  createHeadingWorkflowInstance,
  createWorkflowInstanceOnDocument,
  forceWorkflowToStage,
  getCompoundRole,
  gotoV2Document,
  grantMemberRoleToGroup,
  loadAllGroups,
  // load2AllGroups,
  loadAssignableGroupsInProject,
  loadAvailableUsersInProject,
  loadDocGroup,
  // loadCurrentInstance,
  loadCurrentUserActions,
  loadCurrentWorkflowInstance,
  loadV2Headings,
  loadProjectWorkflowModelId,
  loadWidgetConfig,
  lockDocument,
  processWorkflowAction,
  progressWorkflowStage,
  regressWorkflowStage,
  removeRoleFromComposite,
  revokeRoleFromGroup,
  updateDocGroupUsers,
  updateWorkflowInstance,
  archiveHeadingWorkflow,
  loadHeadingWorkflowId,
  loadHeadingWorkflowModel,
  v2WorkflowActionResult,
  assignV2DocumentMembers,
  assignV2DocumentAssignableUsers,
  assignV2DocumentAssignableGroups,
} from "./workflowWidget.api";
import noop from "lodash.noop";
import { DateTime } from "luxon";
import { equals } from "ramda";

import {
  getId, injectApiWithContextFactory, injectBoundActionCreatorsFactory, takeFirstAndLatest, takeLatestByScope, withRefreshRequest 
} from "Utils/sagaUtils";
import { ONE_SECOND } from "Globals/constants/constants";

import { injectBoundGQLClient } from "Utils/injectBoundGQLClient";

import {
  FORCE_WORKFLOW_STAGE_REQUESTED,
  GET_COMPOUND_ROLE_REQUESTED,
  GO_TO_V2_DOCUMENT_REQUESTED,
  HEADING_WORKFLOW_ARCHIVE_REQUESTED,
  HEADING_WORKFLOW_CREATE_REQUESTED,
  HEADING_WORKFLOW_PROGRESS_STAGE_REQUESTED,
  HEADING_WORKFLOW_REGRESS_STAGE_REQUESTED,
  LOAD_ASSIGNABLE_USERS_REQUESTED,
  LOAD_CONFIG_FAIL,
  LOAD_CONFIG_REQUESTED,
  LOAD_CONFIG_SUCCESS,
  LOAD_CURRENT_ACTIONS_REQUESTED,
  LOAD_CURRENT_INSTANCE_REQUESTED,
  LOAD_DOCUMENT_GROUP_REQUESTED,
  LOAD_PROJECT_GROUP_REQUESTED,
  PROCESS_WORKFLOW_ACTION_REQUESTED,
  PROGRESS_WORKFLOW_STAGE_REQUESTED,
  UPDATE_DOCUMENT_SETTINGS_REQUESTED,
  UPDATE_WORKFLOW_INSTANCE_REQUESTED,
  UPDATE_WORKFLOW_SETTINGS_REQUESTED,
  failForceWorkflowToStage,
  failGetOrCreateCompoundRole,
  failLoadAssignableUsers,
  failLoadCurrentUserActions,
  failLoadCurrentWorkflowInstance,
  failLoadDocumentGroup,
  failLoadProjectGroup,
  failLoadWorkflowWidgetConfig,
  failProcessAction,
  failProgressWorkflowStage,
  failUpdateDocumentSettings,
  failUpdateWorkflowInstance,
  failUpdateWorkflowSettings,
  requestLoadWorkflowWidgetConfig,
  successForceWorkflowToStage,
  successGetOrCreateCompoundRole,
  successLoadAssignableUsers,
  successLoadCurrentUserActions,
  successLoadCurrentWorkflowInstance,
  successLoadDocumentGroup,
  successLoadProjectGroup,
  successLoadWorkflowWidgetConfig,
  successProcessAction,
  successProgressWorkflowStage,
  successUpdateDocumentSettings,
  successUpdateWorkflowInstance,
  successUpdateWorkflowSettings,
} from "./workflowWidget.duck";

import { requestLoadWorkflowInstance as requestLoadWorkflowInstanceInContext, successLoadWorkflowInstance as successLoadWorkflowInstanceInContext } from "Apps/combinedContextLoader/combinedContextLoader.duck";

export const withBoundActions = injectBoundActionCreatorsFactory(
  {
    failForceWorkflowToStage,
    failGetOrCreateCompoundRole,
    failLoadAssignableUsers,
    failLoadCurrentUserActions,
    failLoadCurrentWorkflowInstance,
    failLoadDocumentGroup,
    failLoadProjectGroup,
    failLoadWorkflowWidgetConfig,
    failProcessAction,
    failProgressWorkflowStage,
    failUpdateDocumentSettings,
    failUpdateWorkflowInstance,
    failUpdateWorkflowSettings,
    requestLoadWorkflowWidgetConfig,
    successForceWorkflowToStage,
    successGetOrCreateCompoundRole,
    successLoadAssignableUsers,
    successLoadCurrentUserActions,
    successLoadCurrentWorkflowInstance,
    successLoadDocumentGroup,
    successLoadProjectGroup,
    successLoadWorkflowWidgetConfig,
    successProcessAction,
    successProgressWorkflowStage,
    successUpdateDocumentSettings,
    successUpdateWorkflowInstance,
    successUpdateWorkflowSettings,
  },
  getId
);

export const withTopLevelContextBoundActions = injectBoundActionCreatorsFactory(
  {
    requestLoadWorkflowInstanceInContext,
  },
  () => "top-level-context-loader"
);

export const withApiContext = injectApiWithContextFactory(
  {
    // load2AllGroups,
    // loadCurrentInstance,
    addRoleToComposite,
    archiveHeadingWorkflow,
    changeDocumentDefaultPermission,
    createCompoundRole,
    createDocumentGroup,
    createHeadingWorkflowInstance,
    createWorkflowInstanceOnDocument,
    forceWorkflowToStage,
    getCompoundRole,
    gotoV2Document,
    grantMemberRoleToGroup,
    loadAllGroups,
    loadAssignableGroupsInProject,
    loadAvailableUsersInProject,
    loadCurrentUserActions,
    loadCurrentWorkflowInstance,
    loadDocGroup,
    loadHeadingWorkflowId,
    loadHeadingWorkflowModel,
    loadProjectWorkflowModelId,
    loadV2Headings,
    loadWidgetConfig,
    lockDocument,
    processWorkflowAction,
    progressWorkflowStage,
    regressWorkflowStage,
    removeRoleFromComposite,
    revokeRoleFromGroup,
    updateDocGroupUsers,
    updateWorkflowInstance,
    v2WorkflowActionResult,
    assignV2DocumentMembers,
    assignV2DocumentAssignableUsers,
    assignV2DocumentAssignableGroups
  },
  injectBoundGQLClient
);

const defaultMatchesFn = (action, taction) => {
  return getId(action) === getId(taction);
};
const takeWithmeta = (actionType, taction, matchesFn = defaultMatchesFn) => {
  return call(function* waitForTakeWithMeta() {
    while (true) {
      const action = yield take(actionType);
      if (matchesFn(action, taction)) {
        return action;
      }
    }
  });
};

const removeTypenameKeys = (obj, keys = ["__typename"]) =>
  obj !== Object(obj)
    ? obj
    : Array.isArray(obj)
      ? obj.map(item => removeTypenameKeys(item, keys))
      : Object.keys(obj)
        .filter(k => !keys.includes(k))
        .reduce((acc, x) => Object.assign(acc, { [x]: removeTypenameKeys(obj[x], keys) }), {});

export function* loadWidgetConfigSaga({
  data, api: { loadWidgetConfig = noop }, actions: { successLoadWorkflowWidgetConfig = noop, failLoadWorkflowWidgetConfig = noop }, mdContext = {}, resolve = noop, reject = noop 
}) {
  try {
    if (mdContext.workflowinstance || data?.workflowinstance) {
      const result = yield loadWidgetConfig({
        workflowid: mdContext.workflowinstance || data?.workflowinstance,
      });
      yield successLoadWorkflowWidgetConfig(result);
      yield call(resolve);
    }
  } catch (e) {
    console.error(e);
    yield failLoadWorkflowWidgetConfig(e);
    yield call(reject, e);
  }
}

function* loadWorkflowInstanceInContextSaga({ actions: { requestLoadWorkflowInstanceInContext = noop }, data, ...rest }) {
  yield requestLoadWorkflowInstanceInContext(data);
}

export function* loadCurrentWorkflowInstanceSaga({
  api: { loadCurrentWorkflowInstance = noop, loadProjectWorkflowModelId = noop, createWorkflowInstanceOnDocument = noop },
  actions: { failLoadCurrentWorkflowInstance = noop, successLoadCurrentWorkflowInstance = noop, successLoadWorkflowWidgetConfig = noop },
  mdContext = {},
  resolve = noop,
  reject = noop,
  meta,
}) {
  try {
    const { allv2Documents, instance } = yield loadCurrentWorkflowInstance({
      id: mdContext.document,
    });
    // console.log({instance, allv2Documents});
    if (!instance?.v2Document){
      const errMsg = "Error: Connection lost";

      if (window?.app?.Duchess?.logNetworkError){
        window?.app?.Duchess?.logNetworkError(errMsg, {}, errMsg);
      } else {
        throw new Error(errMsg);
      }
    }
    if (instance._id) {
      yield successLoadCurrentWorkflowInstance({
        allv2Documents,
        instance,
      });
      yield call(resolve);
      return;
    }
    const workflowModel = yield loadProjectWorkflowModelId();
    const [createdinstance] = yield createWorkflowInstanceOnDocument({
      docId: mdContext.document,
      modelId: workflowModel,
    });
    yield successLoadCurrentWorkflowInstance({
      allv2Documents,
      instance: { ...createdinstance, v2Document: instance.v2Document },
    });
    yield call(withTopLevelContextBoundActions(loadWorkflowInstanceInContextSaga), { data: mdContext.document });
    const config = yield call(withApiContext(withBoundActions(loadWidgetConfigSaga)), { meta, data: { workflowinstance: createdinstance._id } });
    yield successLoadWorkflowWidgetConfig(config);
    yield call(resolve);
  } catch (e) {
    console.error(e);
    yield failLoadCurrentWorkflowInstance(e);
    yield call(reject, e);
  }
}

export function* loadCurrentUserActionsSaga({ api: { loadCurrentUserActions = noop }, actions: { successLoadCurrentUserActions = noop, failLoadCurrentUserActions = noop }, resolve = noop, reject = noop }) {
  try {
    const result = yield loadCurrentUserActions();
    yield successLoadCurrentUserActions(result);
    yield call(resolve);
  } catch (e) {
    console.error(e);
    yield failLoadCurrentUserActions(e);
    yield call(reject, e);
  }
}

export function* progressWorkflowStageSaga(args) {
  const {
    data,
    api: { progressWorkflowStage = noop },
    actions: { successProgressWorkflowStage = noop, failProgressWorkflowStage = noop },
    resolve = noop,
    reject = noop,
    meta,
  } = args;
  try {
    const result = yield progressWorkflowStage(data);
    yield put({ type: LOAD_CONFIG_REQUESTED, meta });
    yield race([ takeWithmeta(LOAD_CONFIG_SUCCESS, args), takeWithmeta(LOAD_CONFIG_FAIL, args) ]);
    yield successProgressWorkflowStage(result);
    yield call(resolve);
  } catch (e) {
    console.error(e);
    yield failProgressWorkflowStage(e);
    yield call(reject, e);
  }
}

export function* forceWorkflowToStageSaga(args) {
  const {
    data,
    api: { forceWorkflowToStage = noop },
    actions: { successForceWorkflowToStage = noop, failForceWorkflowToStage = noop },
    resolve = noop,
    reject = noop,
    meta,
  } = args;
  try {
    const result = yield forceWorkflowToStage(data);
    yield put({ type: LOAD_CONFIG_REQUESTED, meta });
    yield race([ takeWithmeta(LOAD_CONFIG_SUCCESS, args), takeWithmeta(LOAD_CONFIG_FAIL, args) ]);
    yield successForceWorkflowToStage(result);
    yield call(resolve);
  } catch (e) {
    console.error(e);
    yield failForceWorkflowToStage(e);
    yield call(reject, e);
  }
}

export function* processActionSaga(args) {
  const {
    data,
    api: { processWorkflowAction = noop },
    actions: { successProcessAction = noop, failProcessAction = noop },
    resolve = noop,
    reject = noop,
    meta,
  } = args;
  try {
    const result = yield processWorkflowAction(data);
    yield put({ type: LOAD_CONFIG_REQUESTED, meta });
    yield race([ takeWithmeta(LOAD_CONFIG_SUCCESS, args), takeWithmeta(LOAD_CONFIG_FAIL, args) ]);
    yield successProcessAction(result);
    yield call(resolve);
  } catch (e) {
    console.error(e);
    yield failProcessAction(e);
    yield call(reject, e);
  }
}

export function* getOrCreateCompoundRoleSaga({
  data, api: { getCompoundRole = noop }, actions: { successGetOrCreateCompoundRole = noop, failGetOrCreateCompoundRole = noop }, resolve = noop, reject = noop, mdContext: { instance, project, document } 
}) {
  try {
    const roleName = `${instance}_${project}_v2document_${document}`;
    const result = yield getCompoundRole({ roleName });
    yield successGetOrCreateCompoundRole(result);
    yield call(resolve);
  } catch (e) {
    console.error(e);
    yield failGetOrCreateCompoundRole(e);
    yield call(reject, e);
  }
}

// eslint-disable-next-line max-statements
export function* updateWorkflowSettingsSaga(args) {
  const {
    data: {
      assignedUsers = [], docGroup, workflowStages, instance, ...rest 
    },
    api: { updateDocGroupUsers = noop, updateWorkflowInstance = noop, grantMemberRoleToGroup = noop, revokeRoleFromGroup = noop },
    actions: { successUpdateWorkflowSettings = noop, failUpdateWorkflowSettings = noop },
    resolve = noop,
    reject = noop,
    mdContext,
    meta,
  } = args;
  try {
    const documentAssignedGroups = assignedUsers?.filter(({ kind }) => kind === "ProjectGroup") || [];
    const documentAssignedUsers = assignedUsers?.filter(({ kind }) => kind === "User");

    const currentUsers = docGroup.members?.filter(({ __typename }) => __typename === "User") || [];
    const currentGroups = docGroup.members?.filter(({ __typename }) => __typename === "ProjectGroup") || [];

    const initialDocGroupUsers = currentUsers?.map(({ _id }) => _id) || [];
    const updateUserIds = documentAssignedUsers?.map(({ value }) => value) || [];
    const removeUserIds = initialDocGroupUsers?.filter(id => !updateUserIds.includes(id)) || [];
    const addUserIds = updateUserIds?.filter(id => !initialDocGroupUsers.includes(id)) || [];
    const shouldUpdateUsers = removeUserIds.length > 0 || addUserIds.length > 0;

    const updatedDocGroup = shouldUpdateUsers
      ? yield updateDocGroupUsers({
        groupId: docGroup.id,
        removeUserIds,
        addUserIds,
      })
      : {
        ...docGroup,
        members: currentUsers,
      };

    const initialAssignedGroups = currentGroups?.map(({ id }) => id) || [];
    const updateGroupIds = documentAssignedGroups?.map(({ value }) => value) || [];
    const removeGroupIds = initialAssignedGroups?.filter(id => !updateGroupIds.includes(id)) || [];
    const addGroupIds = updateGroupIds?.filter(id => !initialAssignedGroups.includes(id)) || [];

    let role;
    for (const group of addGroupIds) {
      role = yield grantMemberRoleToGroup({ roleIdentifier: `v2document:${mdContext.document}:member`, groupId: group });
    }
    for (const group of removeGroupIds) {
      role = yield revokeRoleFromGroup({ roleIdentifier: `v2document:${mdContext.document}:member`, groupId: group });
    }

    let rotateStage = false;

    const permissionsMap = {
      editor: ["nothing"],
      commenter: ["editor"],
      viewer: [ "editor", "commenter" ],
    };

    const defaultRole = `v2document:${mdContext.project}-${instance?.v2Document?.id}:viewer`;

    const updatedInstance = {
      name: instance.name || "",
      description: instance.description,
      StartDate: instance.StartDate,
      defaultRole,
      stages: instance.stages.map(stage => {
        // eslint-disable-next-line no-underscore-dangle
        const settingsStage = workflowStages[stage._id];
        const isApprovalStage = stage?.onEnter?.createActions?.some(({ kind }) => kind === "APPROVE");
        const stageAssignedUsers = settingsStage.users
          .filter(i => !!i)
          .map(({ _id }) => assignedUsers.find(({ value }) => value === _id))
          .filter(i => !!i)
          .map(({ __typename, kind, value }) => ({
            _id: value,
            kind: kind || __typename,
          }));
        const explicit_deny = settingsStage.users
          .filter(i => !!i)
          .filter(({ _id }) => _id)
          .flatMap(({ _id, permission = "editor" }) => {
            const permissionsToDeny = permissionsMap[permission] || [];
            // eslint-disable-next-line no-underscore-dangle
            const fullUser = assignedUsers.find(({ value }) => value === _id);
            const { name: role } = fullUser?.groups?.find(({ hasRoles, hasChildGroups, name }) => hasRoles && !hasChildGroups && [
              "ADMINISTRATOR", "COLLABORATOR", "REVIEWER"
            ].includes(name)) || {};

            // eslint-disable-next-line no-underscore-dangle
            const kind = fullUser?.kind || fullUser?.__typename || "User";

            const getEnforcedUserRolePermissions = (role, permissions) => {
              if (role === "ADMINISTRATOR") return ["nothing"];
              if (role === "REVIEWER") return [...new Set([ ...permissions, "editor" ])].filter(p => p !== "nothing");
              return permissions;
            };

            return getEnforcedUserRolePermissions(role, permissionsToDeny).map(perm => ({
              subject: _id,
              permission: perm,
              kind,
              scope: "v2document",
            }));
          });
        const createActions =
                    stage?.onEnter?.createActions.map(act => ({
                      ...act,
                      subject: stageAssignedUsers.map(({ _id, kind }) => ({ id: _id, kind })) || [],
                    })) || [];
        if (!equals(explicit_deny, stage.explicit_deny) || !equals(createActions, stage?.onEnter?.createActions)) {
          rotateStage = true;
        }

        const dueDate = (settingsStage.dueDate || stage.dueDate) && DateTime.fromFormat(settingsStage.dueDate || stage.dueDate, "yyyy-MM-dd").toISO();

        const ALL = 0; // required count=0 means 'ALL' aka no optn action left
        return {
          id: stage._id,
          autoprogress:
                        stage.autoprogress.map(guard => ({
                          nextStage: guard?.nextStage?._id,
                          conditions: guard.conditions.map(cond => ({
                            ...cond,
                            assertation: {
                              ActionAssertationInput: {
                                ...cond.assertation,
                                customStatus: undefined,
                                customstatus: cond?.assertation?.customStatus,
                                count: isApprovalStage
                                  ? stageAssignedUsers?.length > 0
                                    ? ALL
                                    : 1 // require ALL, but at least 1 approval to autoprogress
                                  : 1,
                              },
                            },
                          })),
                        })) || [],
          defaultActionType: stage.defaultActionType,
          description: stage.description,
          dueDate,
          entryguards: stage.entryguards || [],
          exitguards: stage.exitguards || [],
          name: stage.name,
          onEnter: {
            createActions,
            explicit_deny,
          },
        };
      }),
    };

    const updatedWorkflowInstance = yield updateWorkflowInstance({
      // eslint-disable-next-line no-underscore-dangle
      instanceId: instance._id,
      updatedInstance: { ...removeTypenameKeys(updatedInstance) },
      rotateStage,
    });

    yield put({ type: LOAD_CONFIG_REQUESTED, meta });
    yield race([ takeWithmeta(LOAD_CONFIG_SUCCESS, args), takeWithmeta(LOAD_CONFIG_FAIL, args) ]);

    yield successUpdateWorkflowSettings({
      docGroup: updatedDocGroup,
      instance: updatedWorkflowInstance,
      updateGroupIds,
      removeGroupIds,
      role,
    });
    yield call(resolve);
  } catch (e) {
    console.error(e);
    yield failUpdateWorkflowSettings(e);
    yield call(reject, e);
  }
}

export function* updateWorkflowInstanceSaga({
  data: { update, instance }, api: { updateWorkflowInstance = noop, lockDocument = noop }, actions: { successUpdateWorkflowInstance = noop, failUpdateWorkflowInstance = noop }, resolve = noop, reject = noop 
}) {
  try {
    const updateFields = Object.keys(update);
    if (updateFields.length === 1 && updateFields[0] === "locked" && instance?.v2Document?.id) {
      const targetState = !instance?.v2Document?.locked;
      yield lockDocument({ docId: instance?.v2Document.id, targetState });
      yield successUpdateWorkflowInstance(instance);
      yield call(resolve);
    } else {
      const updatedInstance = {
        name: instance.name,
        description: instance.description,
        StartDate: instance.StartDate,
        stages: instance.stages.map(stage => {
          const onEnter = {
            ...stage.onEnter,
            createActions:
                            stage?.onEnter?.createActions.map(({ subject, ...restAction }) => ({
                              ...restAction,
                              subject: subject.map(({ assignee, ...restSub }) => ({
                                ...restSub,
                              })),
                            })) || [],
          };
          const autoprogress =
                        stage?.autoprogress?.map(guard => ({
                          nextStage: guard?.nextStage?._id,
                          conditions: guard.conditions.map(cond => ({
                            ...cond,
                            assertation: {
                              ActionAssertationInput: {
                                ...cond.assertation,
                                customStatus: undefined,
                                customstatus: cond.assertation.customStatus,
                                count: Math.max(0, cond.assertation?.count),
                              },
                            },
                          })),
                        })) || [];
          // eslint-disable-next-line no-underscore-dangle
          if (stage._id === instance.currentStage?._id) {
            return {
              id: stage._id,
              autoprogress: autoprogress || [],
              defaultActionType: update.defaultActionType || stage.defaultActionType,
              description: update.description || stage.description,
              dueDate: DateTime.fromFormat(update.dueDate, "yyyy-MM-dd").toISO() || stage.dueDate,
              entryguards: stage.entryguards || [],
              exitguards: stage.exitguards || [],
              name: update.name || stage.name || "",
              onEnter: onEnter || [],
            };
          }
          return {
            id: stage._id,
            autoprogress: autoprogress || [],
            defaultActionType: stage.defaultActionType || "APPROVE",
            description: stage.description,
            dueDate: stage.dueDate,
            entryguards: stage.entryguards || [],
            exitguards: stage.exitguards || [],
            name: stage.name || "",
            onEnter: onEnter || [],
          };
        }),
      };
      // eslint-disable-next-line no-underscore-dangle
      const result = yield updateWorkflowInstance({
        instanceId: instance._id,
        updatedInstance: { ...removeTypenameKeys(updatedInstance) },
        rotateStage: true,
      });
      yield successUpdateWorkflowInstance(result);
      yield call(resolve);
    }
  } catch (e) {
    console.error(e);
    yield failUpdateWorkflowInstance(e);
    yield call(reject, e);
  }
}

// eslint-disable-next-line max-statements
export function* updateDocumentSettingsSaga(args) {
  const {
    data: { assignedUsers, docGroup, defaultDocRole },
    api: {
      updateDocGroupUsers = noop, changeDocumentDefaultPermission = noop, grantMemberRoleToGroup = noop, revokeRoleFromGroup = noop, assignV2DocumentMembers = noop  
    },
    actions: { successUpdateDocumentSettings = noop, failUpdateDocumentSettings = noop },
    resolve = noop,
    reject = noop,
    mdContext,
  } = args;
  try {
    const documentAssignedGroups = assignedUsers.filter(({ kind }) => kind === "ProjectGroup");
    const documentAssignedUsers = assignedUsers.filter(({ kind }) => kind === "User");

    const currentUsers = docGroup.members.filter(({ __typename }) => __typename === "User");
    const currentGroups = docGroup.members.filter(({ __typename }) => __typename === "ProjectGroup");

    const initialDocGroupUsers = currentUsers.map(({ _id }) => _id);
    const updateUserIds = documentAssignedUsers.map(({ value }) => value);
    const removeUserIds = initialDocGroupUsers.filter(id => !updateUserIds.includes(id));
    const addUserIds = updateUserIds.filter(id => !initialDocGroupUsers.includes(id));
    const shouldUpdateUsers = removeUserIds.length > 0 || addUserIds.length > 0;

    const updatedDocGroup = shouldUpdateUsers
      ? yield updateDocGroupUsers({
        groupId: docGroup.id,
        removeUserIds,
        addUserIds,
      })
      : {
        ...docGroup,
        members: currentUsers,
      };

    const initialAssignedGroups = currentGroups.map(({ id }) => id);
    const updateGroupIds = documentAssignedGroups.map(({ value }) => value);
    const removeGroupIds = initialAssignedGroups.filter(id => !updateGroupIds.includes(id));
    const addGroupIds = updateGroupIds.filter(id => !initialAssignedGroups.includes(id));

    let role;
    for (const group of addGroupIds) {
      role = yield grantMemberRoleToGroup({ roleIdentifier: `v2document:${mdContext.document}:member`, groupId: group });
    }
    for (const group of removeGroupIds) {
      role = yield revokeRoleFromGroup({ roleIdentifier: `v2document:${mdContext.document}:member`, groupId: group });
    }

    const permission = `v2document:${mdContext.document}:${defaultDocRole}`;

    const updatedInstance = yield changeDocumentDefaultPermission({ permission });

    yield successUpdateDocumentSettings({
      docGroup: updatedDocGroup,
      instance: updatedInstance,
      role,
      updateGroupIds,
      removeGroupIds,
    });

    yield assignV2DocumentMembers({ members: updatedDocGroup?.members || []});

    yield call(resolve);
  } catch (e) {
    console.error(e);
    yield failUpdateDocumentSettings(e);
    yield call(reject, e);
  }
}

export function* goToV2DocumentSaga({ data: docId, api: { gotoV2Document = noop } }) {
  try {
    yield gotoV2Document({ docId });
  } catch (e) {
    console.error(e);
  }
}

// eslint-disable-next-line max-statements
export function* loadCurrentDocumentGroupSaga({
  api: {
    loadDocGroup = noop,
    createDocumentGroup = noop,
    grantMemberRoleToGroup = noop,
    loadAllGroups = noop,
    assignV2DocumentMembers = noop,
    assignV2DocumentAssignableUsers = noop,
    loadAvailableUsersInProject = noop,
    loadAssignableGroupsInProject = noop,
  },
  actions: { successLoadDocumentGroup = noop, failLoadDocumentGroup = noop },
  resolve = noop,
  reject = noop,
  mdContext = {},
}) {
  try {
    const { project } = mdContext;
    const [docGroup] = yield loadDocGroup({ docId: mdContext.document });
    // const { docGroup } = yield loadAllGroups({docId: mdContext.document});
    // yield successLoadProjectGroup(assignableGroups)
    // yield successLoadAssignableUsers(assignableUsers)

    if (docGroup) {
      const hasMemberRole = docGroup.roles?.find(({ name }) => name === `v2document:${mdContext.document}:member`);
      if (!hasMemberRole) {
        yield grantMemberRoleToGroup({ roleIdentifier: `v2document:${mdContext.document}:member`, groupId: docGroup.id });
      }
      const projectGroup = { ...docGroup, members: docGroup.members.map(user => {
        return {
          ...user,
          groups: user?.groups?.filter(g => g.parentId === project)
        };
      }) };

      yield successLoadDocumentGroup({
        docGroup: projectGroup,
      });

      const availableusers = yield loadAvailableUsersInProject();
      // Its a hack to work with other components because using edges and node
      // Fix the bug where user not show up in the dropdown if it has disabled-role from other project
      const users = removeDuplicates(
        availableusers
          .map(user => ({
            ...user,
            groups: user?.groups?.filter(({ parentId }) => parentId === mdContext.project),
          }))
          .filter(({ groups }) => !groups.some(({ name }) => name.includes("-DISABLED"))),
        "_id"
      );
      // const assignableUsers = users?.map(item => ({ node: item })) || [];
      const assignableGroups = yield loadAssignableGroupsInProject();

      yield successLoadProjectGroup(assignableGroups);

      yield assignV2DocumentAssignableUsers({ assignableUsers: users });
      yield assignV2DocumentMembers({ members: projectGroup?.members || []});
      yield assignV2DocumentAssignableGroups({ assignableGroups: assignableGroups || []});
      
      yield call(resolve);
    } else {
      throw new Error(`Docgroup not found for document ${mdContext.document}.`);
    }
  } catch (e) {
    console.error(e);
    yield failLoadDocumentGroup(e);
    yield call(reject, e);
  }
}

export function* loadAssignableGroupsInProjectSaga({
  api: { loadAssignableGroupsInProject = noop, assignV2DocumentAssignableGroups = noop }, actions: { failLoadProjectGroup = noop, successLoadProjectGroup = noop }, resolve = noop, reject = noop, mdContext = {} 
}) {
  try {
    const assignableGroups = yield loadAssignableGroupsInProject();

    yield successLoadProjectGroup(assignableGroups);
    yield assignV2DocumentAssignableGroups({ assignableGroups: assignableGroups || []});
    yield call(resolve);
  } catch (e) {
    console.error(e);
    yield failLoadProjectGroup(e);
    yield call(reject, e);
  }
}

const removeDuplicates = (array, key) => {
  return array?.reduce((arr, item) => {
    const removed = arr.filter(i => i[key] !== item[key]);
    return [ ...removed, item ];
  }, []);
};

export function* loadAvailableUsersInProjectSaga({
  api: { loadAvailableUsersInProject = noop },
  actions: { failLoadAssignableUsers = noop, successLoadAssignableUsers = noop }, resolve = noop, reject = noop, mdContext = {} 
}) {
  try {
    const availableusers = yield loadAvailableUsersInProject();
    // Its a hack to work with other components because using edges and node
    // Fix the bug where user not show up in the dropdown if it has disabled-role from other project
    const users = removeDuplicates(
      availableusers
        .map(user => ({
          ...user,
          groups: user?.groups?.filter(({ parentId }) => parentId === mdContext.project),
        }))
        .filter(({ groups }) => !groups.some(({ name }) => name.includes("-DISABLED"))),
      "_id"
    );
    const assignableUsers = users?.map(item => ({ node: item })) || [];

    yield successLoadAssignableUsers(assignableUsers);
    yield assignV2DocumentAssignableUsers({ assignableUsers });
    yield call(resolve);
  } catch (e) {
    console.error(e);
    yield failLoadAssignableUsers(e);
    yield call(reject, e);
  }
}

export function* createHeadingWorkflowSaga({
  data, api: { loadV2Headings = noop, createHeadingWorkflowInstance = noop, loadHeadingWorkflowModel = noop, v2WorkflowActionResult = noop }, resolve = noop, reject = noop, mdContext = {} 
}) {
  try {
    const { v3ProjectId, v2DocumentId, v2HeadingId } = data || {};
    const workflowModel = yield loadHeadingWorkflowModel({ v3ProjectId });
    if (workflowModel?.defaultDisabled) {
      yield call(resolve);
    } else {
      const headings = yield loadV2Headings(mdContext || {});

      const headingWorkflowIDs = (headings || []).map(({ id }) => `${mdContext?.project}:${mdContext?.document.split("-")[1]}-${id}`);
      if (v3ProjectId && v2DocumentId && v2HeadingId) {
        headingWorkflowIDs.push(`${v3ProjectId}:${v2DocumentId}-${v2HeadingId}`);
      }

      yield createHeadingWorkflowInstance({ ids: headingWorkflowIDs, modelId: workflowModel?._id, v3ProjectId });
      yield v2WorkflowActionResult({ actionResult: { documentId: v2DocumentId, headingIds: [v2HeadingId], success: true } });
      yield call(resolve);
    }
  } catch (e) {
    console.error(e);
    yield v2WorkflowActionResult({ actionResult: { success: false, message: e } });
    yield call(reject, e);
  }
}

export function* archiveHeadingWorkflowSaga({ data, api: { archiveHeadingWorkflow = noop, v2WorkflowActionResult = noop, loadHeadingWorkflowId = noop }, resolve = noop, reject = noop }) {
  try {
    const { v3ProjectId, v2DocumentId, v2HeadingId } = data;
    if (!v2HeadingId || !v3ProjectId || !v2DocumentId) {
      throw new Error(`undefined detected either in v2HeadingId(${v2HeadingId}) and v3ProjectId(${v3ProjectId}).`);
    }
    const workflowModel = yield loadHeadingWorkflowId();
    if (workflowModel?.defaultDisabled) {
      yield call(resolve);
    } else {
      yield archiveHeadingWorkflow({ v3ProjectId, v2DocumentId, v2HeadingId });
      yield v2WorkflowActionResult({ actionResult: { documentId: v2DocumentId, headingIds: [v2HeadingId], success: true } });
      yield call(resolve);
    }
  } catch (e) {
    console.error(e);
    yield v2WorkflowActionResult({ actionResult: { success: false, message: e } });
    yield call(reject, e);
  }
}

export function* progressHeadingWorkflowSaga(args) {
  const {
    data,
    api: { progressWorkflowStage = noop, v2WorkflowActionResult = noop },
    resolve = noop,
    reject = noop,
  } = args;
  try {
    const result = yield progressWorkflowStage(data);
    const documentId = Number(result._parent._id.split("-")[0].split(":")[1]);
    const headingId = Number(result._parent._id.split("-")[1]);
    yield v2WorkflowActionResult({ actionResult: { documentId, headingIds: [headingId], success: true } });
    yield call(resolve);
  } catch (e) {
    console.error(e);
    yield v2WorkflowActionResult({ actionResult: { success: false } });
    yield call(reject, e);
  }
}

export function* regressHeadingWorkflowSaga(args) {
  const {
    data,
    api: { regressWorkflowStage = noop, v2WorkflowActionResult = noop },
    resolve = noop,
    reject = noop,
  } = args;
  try {
    const result = yield regressWorkflowStage(data);
    const documentId = Number(result._parent._id.split("-")[0].split(":")[1]);
    const headingId = Number(result._parent._id.split("-")[1]);
    yield v2WorkflowActionResult({ actionResult: { documentId, headingIds: [headingId], success: true } });
    yield call(resolve);
  } catch (e) {
    console.error(e);
    yield v2WorkflowActionResult({ actionResult: { success: false } });
    yield call(reject, e);
  }
}

export default () =>
  function* rootSaga() {
    yield takeLatestByScope(
      LOAD_DOCUMENT_GROUP_REQUESTED,
      // withRefreshRequest(
      withApiContext(withBoundActions(loadCurrentDocumentGroupSaga))
      // )
    );
    yield takeLatestByScope(LOAD_CURRENT_INSTANCE_REQUESTED, withRefreshRequest(withApiContext(withBoundActions(loadCurrentWorkflowInstanceSaga))));
    yield takeLatestByScope(LOAD_CONFIG_REQUESTED, withRefreshRequest(withApiContext(withBoundActions(loadWidgetConfigSaga))));
    yield takeLatestByScope(
      LOAD_CURRENT_ACTIONS_REQUESTED,
      // withRefreshRequest(
      withApiContext(withBoundActions(loadCurrentUserActionsSaga))
      // )
    );

    yield takeLatestByScope(GET_COMPOUND_ROLE_REQUESTED, withRefreshRequest(withApiContext(withBoundActions(getOrCreateCompoundRoleSaga))));
    yield takeEvery(LOAD_PROJECT_GROUP_REQUESTED, withApiContext(withBoundActions(loadAssignableGroupsInProjectSaga)));
    yield takeEvery(LOAD_ASSIGNABLE_USERS_REQUESTED, withApiContext(withBoundActions(loadAvailableUsersInProjectSaga)));
    yield takeLatestByScope(PROGRESS_WORKFLOW_STAGE_REQUESTED, withApiContext(withBoundActions(progressWorkflowStageSaga)));
    yield takeEvery(GO_TO_V2_DOCUMENT_REQUESTED, withApiContext(withBoundActions(goToV2DocumentSaga)));
    yield takeEvery(FORCE_WORKFLOW_STAGE_REQUESTED, withApiContext(withBoundActions(forceWorkflowToStageSaga)));
    yield takeEvery(PROCESS_WORKFLOW_ACTION_REQUESTED, withApiContext(withBoundActions(processActionSaga)));
    yield takeEvery(UPDATE_WORKFLOW_INSTANCE_REQUESTED, withApiContext(withBoundActions(updateWorkflowInstanceSaga)));
    yield takeEvery(UPDATE_WORKFLOW_SETTINGS_REQUESTED, withApiContext(withBoundActions(updateWorkflowSettingsSaga)));
    yield takeEvery(UPDATE_DOCUMENT_SETTINGS_REQUESTED, withApiContext(withBoundActions(updateDocumentSettingsSaga)));
    yield takeEvery(HEADING_WORKFLOW_CREATE_REQUESTED, withApiContext(withBoundActions(createHeadingWorkflowSaga)));
    yield takeEvery(HEADING_WORKFLOW_ARCHIVE_REQUESTED, withApiContext(withBoundActions(archiveHeadingWorkflowSaga)));
    yield takeEvery(HEADING_WORKFLOW_PROGRESS_STAGE_REQUESTED, withApiContext(withBoundActions(progressHeadingWorkflowSaga)));
    yield takeEvery(HEADING_WORKFLOW_REGRESS_STAGE_REQUESTED, withApiContext(withBoundActions(regressHeadingWorkflowSaga)));
  };
